import "./SpeakerReader.css";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatDurationSeconds,
  getSpeakerNotesForSlide,
  resolveTargetSeconds,
  type SurfDeck,
  type SurfSlide,
} from "@vida/surf-core";
import { saveReturnPath } from "../../auth";
import { useSurfAssetUrls } from "../../hooks/useSurfAssetUrls";
import { speakerReaderPath } from "../../utils/speakerReaderLink";
import { PresentMode } from "../present/PresentMode";
import { SlideCanvas } from "../editor/SlideCanvas";
import { SpeakerNoteCard } from "../editor/SpeakerNoteCard";
import { SpeakerScriptView } from "../editor/SpeakerScriptEditor";
import { useReaderPrefetch } from "./useReaderPrefetch";
import { LAST_READER_KEY } from "../../utils/speakerReaderConstants";

const CHROME_IDLE_MS = 3000;
/** Max SlideCanvas instances alive (current ± this many neighbors). */
const SLIDE_WINDOW = 1;

type Props = {
  deck: SurfDeck;
  documentId: string;
};

function slideLabel(slide: SurfSlide, index: number): string {
  const title = slide.layers
    .find((l) => l.type === "text" && l.textRole === "title")
    ?.content?.trim();
  return title || `Slide ${index + 1}`;
}

export function SpeakerReader({ deck, documentId }: Props) {
  const { backgroundUrls, layerImageUrls } = useSurfAssetUrls(deck, { readerMode: true });
  const total = deck.slides.length;
  const [index, setIndex] = useState(0);
  const [presentOpen, setPresentOpen] = useState(false);
  const [presentIndex, setPresentIndex] = useState(0);
  const [chromeHidden, setChromeHidden] = useState(false);
  const carouselRef = useRef<HTMLElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollSyncRef = useRef(false);

  const atStart = index <= 0;
  const atEnd = index >= total - 1;

  useReaderPrefetch(deck);

  useEffect(() => {
    const path = speakerReaderPath(documentId);
    localStorage.setItem(LAST_READER_KEY, path);
    saveReturnPath(path);
  }, [documentId]);

  const bumpActivity = useCallback(() => {
    setChromeHidden(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setChromeHidden(true), CHROME_IDLE_MS);
  }, []);

  useEffect(() => {
    bumpActivity();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [bumpActivity]);

  const scrollToSlide = useCallback((i: number, behavior: ScrollBehavior = "smooth") => {
    const clamped = Math.max(0, Math.min(total - 1, i));
    const root = carouselRef.current;
    scrollSyncRef.current = true;
    setIndex(clamped);
    if (root) {
      root.scrollTo({ left: clamped * root.clientWidth, behavior });
    }
    bumpActivity();
    window.setTimeout(() => {
      scrollSyncRef.current = false;
    }, behavior === "smooth" ? 450 : 50);
  }, [bumpActivity, total]);

  const goPrev = useCallback(() => {
    scrollToSlide(index - 1);
  }, [index, scrollToSlide]);

  const goNext = useCallback(() => {
    scrollToSlide(index + 1);
  }, [index, scrollToSlide]);

  useEffect(() => {
    const root = carouselRef.current;
    if (!root) return;

    const onScroll = () => {
      if (scrollSyncRef.current) return;
      const w = root.clientWidth;
      if (w <= 0) return;
      const next = Math.max(0, Math.min(total - 1, Math.round(root.scrollLeft / w)));
      setIndex((prev) => (prev === next ? prev : next));
      bumpActivity();
    };

    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, [bumpActivity, total]);

  useEffect(() => {
    const root = carouselRef.current;
    if (!root) return;
    const ro = new ResizeObserver(() => {
      scrollSyncRef.current = true;
      root.scrollLeft = index * root.clientWidth;
      window.setTimeout(() => {
        scrollSyncRef.current = false;
      }, 50);
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (presentOpen) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true]")) return;

      if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        goNext();
      } else if (e.key === "Home") {
        e.preventDefault();
        scrollToSlide(0);
      } else if (e.key === "End") {
        e.preventDefault();
        scrollToSlide(total - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, presentOpen, scrollToSlide, total]);

  const indexItems = useMemo(
    () =>
      deck.slides.map((s, i) => ({
        id: s.id,
        label: slideLabel(s, i),
        number: i + 1,
      })),
    [deck.slides],
  );

  if (!total) {
    return null;
  }

  return (
    <div
      className={`speaker-reader${chromeHidden ? " speaker-reader--chrome-hidden" : ""}`}
      onPointerMove={bumpActivity}
    >
      <header className="speaker-reader-header">
        <div className="speaker-reader-header-main">
          <Link to={`/deck/${documentId}`} className="speaker-reader-back">
            ← Editor
          </Link>
          <h1 className="speaker-reader-title">{deck.title}</h1>
          <p className="speaker-reader-subtitle muted">
            Lettore speaker · slide {index + 1} di {total}
          </p>
        </div>
        {!navigator.onLine && (
          <p className="speaker-reader-offline-badge" role="status">
            Offline — contenuti dalla cache locale
          </p>
        )}
      </header>

      <nav className="speaker-reader-index" aria-label="Indice slide">
        {indexItems.map((item, i) => (
          <button
            key={item.id}
            type="button"
            className={`speaker-reader-index-btn${i === index ? " is-active" : ""}`}
            onClick={() => scrollToSlide(i)}
            title={item.label}
            aria-current={i === index ? "true" : undefined}
          >
            <span className="speaker-reader-index-num">{item.number}</span>
            <span className="speaker-reader-index-label">{item.label}</span>
          </button>
        ))}
      </nav>

      <main
        className="speaker-reader-carousel"
        ref={carouselRef}
        aria-label="Slide del discorso"
      >
        {deck.slides.map((pageSlide, slideIndex) => {
          const pageNotes = getSpeakerNotesForSlide(deck, pageSlide.id);
          const pageScript = pageSlide.speakerScript?.text?.trim() ?? "";
          const pageTargetSeconds = resolveTargetSeconds(deck, pageSlide.id);
          const pageLabel = slideLabel(pageSlide, slideIndex);
          const inWindow = Math.abs(slideIndex - index) <= SLIDE_WINDOW;

          return (
            <section
              key={pageSlide.id}
              className="speaker-reader-slide-page"
              aria-hidden={slideIndex !== index}
              aria-label={`Slide ${slideIndex + 1}: ${pageLabel}`}
            >
              <div className="speaker-reader-main">
                <div className="speaker-reader-slide-head">
                  <span className="speaker-reader-slide-num">{slideIndex + 1}</span>
                  <h2 className="speaker-reader-slide-heading">{pageLabel}</h2>
                  <button
                    type="button"
                    className="surf-btn surf-btn--ghost speaker-reader-present-btn"
                    onClick={() => {
                      setPresentIndex(slideIndex);
                      setPresentOpen(true);
                    }}
                  >
                    Schermo intero
                  </button>
                </div>

                <div className="speaker-reader-slide-grid">
                  <div className="speaker-reader-thumb-wrap">
                    <div className="speaker-reader-thumb">
                      {inWindow ? (
                        <SlideCanvas
                          deck={deck}
                          slide={pageSlide}
                          slideBackgroundUrl={backgroundUrls[pageSlide.id]}
                          layerImageUrls={layerImageUrls}
                          selectedLayerIds={[]}
                          showGuides={false}
                          readOnly
                          compact
                          onSelectLayer={() => undefined}
                          onLayerRect={() => undefined}
                          onLayerContent={() => undefined}
                        />
                      ) : (
                        <div className="speaker-reader-thumb-placeholder" aria-hidden />
                      )}
                    </div>
                  </div>

                  <div className="speaker-reader-content">
                    <section className="speaker-reader-script" aria-label="Discorso speaker">
                      <div className="speaker-reader-script-head">
                        <h3>Discorso</h3>
                        {pageTargetSeconds > 0 && (
                          <span className="speaker-reader-duration muted">
                            ~{formatDurationSeconds(pageTargetSeconds)}
                          </span>
                        )}
                      </div>
                      {pageScript ? (
                        <SpeakerScriptView
                          markdown={pageScript}
                          className="speaker-reader-script-md"
                        />
                      ) : (
                        <p className="muted">Nessun discorso per questa slide.</p>
                      )}
                    </section>

                    {pageNotes.length > 0 && (
                      <section className="speaker-reader-notes" aria-label="Approfondimenti">
                        <h3 className="speaker-reader-notes-title">Approfondimenti</h3>
                        <div className="speaker-reader-notes-list">
                          {pageNotes.map((note, noteIndex) => (
                            <SpeakerNoteCard
                              key={note.id}
                              deck={deck}
                              slideId={pageSlide.id}
                              note={note}
                              index={noteIndex}
                              readOnly
                              defaultMinimized
                              resetCollapseKey={index}
                              onDeckChange={() => undefined}
                              onDragStart={() => undefined}
                              onDragOver={() => undefined}
                              onDrop={() => undefined}
                            />
                          ))}
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </main>

      <footer className="speaker-reader-nav" aria-label="Navigazione slide">
        <button
          type="button"
          className="surf-btn surf-btn--ghost speaker-reader-nav-btn"
          disabled={atStart}
          onClick={goPrev}
          aria-label="Slide precedente"
        >
          ◀ Precedente
        </button>
        <span className="speaker-reader-nav-counter muted">
          {index + 1} / {total}
        </span>
        <button
          type="button"
          className="surf-btn surf-btn--ghost speaker-reader-nav-btn"
          disabled={atEnd}
          onClick={goNext}
          aria-label="Slide successiva"
        >
          Successiva ▶
        </button>
      </footer>

      {presentOpen && (
        <PresentMode
          deck={deck}
          initialIndex={presentIndex}
          slideBgUrls={backgroundUrls}
          layerImageUrls={layerImageUrls}
          onClose={(finalIndex) => {
            setPresentOpen(false);
            scrollToSlide(finalIndex, "auto");
          }}
        />
      )}
    </div>
  );
}
